/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  Typography,
  Grid,
} from '@mui/material'
import React, { useState } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  gridClasses,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import {
  getUserActionsSearchLogs,
  type UserActionsResponse,
  type UserAction,
} from '../services'
import AuditUserActionsFilters from './AuditUserActionsFilters'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'

interface UserActionRow extends UserAction {
  readonly 'user:representation_name': string
  readonly 'user_action:name': string
  readonly action: string
  readonly origin: string
}

// eslint-disable-next-line max-lines-per-function
export default function ListAllAuditUserActions() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const selectAction = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const userId = useHookstate<number | null>(null)
  const stationId = useHookstate<number | null>(null)
  const IdDispatch = useHookstate<number | null>(null)
  const resetValues = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const limit = 15
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])
  const tableMode = 'server'
  const expandUserActions = useHookstate<boolean>(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] = useState<UserActionRow | null>(null)
  const originType = useHookstate<string | null>(null) // Alterado para string | null
  const selectedDevice = useHookstate<number | null>(null)
  const selectedStations = useHookstate<number | null>(null)

  const handleView = (row: UserActionRow) => {
    setSelectedRow(row)
    setOpenModal(true)
  }

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setOpenModal(false)
    }
  }

  const columns: readonly GridColDef[] = [
    { field: 'id' },
    {
      field: 'user:representation_name',
      headerName: translate('User'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'action',
      headerName: translate('type action'),
      flex: 1,
      valueFormatter: (value) => translate(value),
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'user_action:name',
      headerName: translate('action'),
      flex: 2,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'origin',
      headerName: translate('Origin'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'created_at',
      headerName: translate('Performed at'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value) =>
        dayjs(value as string).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Tooltip title={translate('Visualizar')}>
            <IconButton
              onClick={() => handleView(params.row)}
              size="small"
              aria-label={translate('Visualizar')}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    selectAction.set(null)
    stationId.set(null)
    IdDispatch.set(null)
    setRows([])
    setSortModel([{ field: 'created_at', sort: 'desc' }])
    setPaginationModel({ page: 0, pageSize: limit })
    expandUserActions.set(false)
    originType.set(null) // Alterado para null
    selectedDevice.set(null)
    selectedStations.set(null)
  }

  const submit = async (
    model = paginationModel,
    currentSortModel: GridSortModel = sortModel
  ) => {
    const hasPeriod = startDate && finishDate
    const hasDispatchId = IdDispatch.get() !== null

    if (!hasPeriod && !hasDispatchId) {
      notify({
        type: 'error',
        message: translate('Period or Dispatch ID is required'),
      })
      return false
    }

    loadingButton.set(true)

    const page = model.page + 1
    const pageSize = model.pageSize

    const orders = currentSortModel.map(({ field, sort }) => ({
      field,
      sort: sort || 'desc',
    }))

    const searchLogData: UserActionsResponse = await getUserActionsSearchLogs({
      user_id: userId.get() || undefined,
      dispatch_id: IdDispatch.get() || undefined,
      origin: originType.get() || undefined, // Alterado para passar undefined quando null
      device_id: selectedDevice.get(),
      station_id: selectedStations.get(),
      start_date: startDate || '',
      finish_date: finishDate || '',
      page: page,
      limit: pageSize,
      orders:
        orders.length > 0 ? orders : [{ field: 'created_at', sort: 'desc' }],
    }).finally(() => {
      loadingButton.set(false)
      expandUserActions.set(true)
    })
    setRows(
      searchLogData.data.map((element: UserAction, index: number) => {
        const generatedId = element.id ?? page * pageSize + index + 1
        return {
          id: generatedId,
          ...element,
          'station:name': element?.station || '',
          'device:name': element?.device || '',
          'user:representation_name': element?.user_name || '',
          'user_action:name': element?.detail || '',
          action: element?.action_type || '',
          search_info: element?.search_info,
          origin: element?.station
            ? `Estação - ${element?.station}`
            : element?.device
            ? `Dispositivo - ${element?.device}`
            : '',
        }
      })
    )

    setRowCount(searchLogData.rowsCount)
  }

  const searchFromFirstPage = () => {
    const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    submit(firstPageModel, sortModel)
  }

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditUserActionsFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          selectAction={selectAction}
          focusChange={focusChange}
          resetValues={resetValues}
          userId={userId}
          stationId={stationId}
          IdDispatch={IdDispatch}
          initialPeriod={initialPeriod}
          originType={originType}
          selectedDevice={selectedDevice}
          selectedStations={selectedStations}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => searchFromFirstPage()}
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() => expandUserActions.set(!expandUserActions.get())}
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandUserActions.value ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandUserActions.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationMode={tableMode}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              submit(model, sortModel)
            }}
            sortingMode={tableMode}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              const firstPageModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
              }
              setPaginationModel(firstPageModel)
              submit(firstPageModel, model)
            }}
            columnVisibilityModel={{
              id: false,
              deleted_at: false,
            }}
            disableColumnFilter
            disableColumnSelector
            autoHeight
            pagination
            filterMode={tableMode}
            rowCount={rowCount}
            getRowId={(row) => Number(row.id)}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <Dialog
        fullWidth
        maxWidth={'md'}
        open={openModal}
        onClose={clickOutModalClose}
        disableEscapeKeyDown={true}
      >
        <DialogTitle
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          {translate('Actions Performed')}
          <IconButton aria-label="close" onClick={() => setOpenModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 5 }}>
          {selectedRow && (
            <Grid container xs={12} display={'flex'} flexDirection={'row'}>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('User')}:
                </Typography>
                <Typography variant="subtitle2">
                  {selectedRow['user:representation_name']}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Type action')}:
                </Typography>
                <Typography variant="subtitle2">
                  {translate(selectedRow.action)}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('action')}:
                </Typography>
                <Typography variant="subtitle2">
                  {selectedRow['user_action:name']}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Performed at')}:
                </Typography>
                <Typography variant="subtitle2">
                  {dayjs(selectedRow.created_at).format('DD/MM/YYYY HH:mm')}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
